"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, ShieldCheck, ClipboardCheck, Users, CircleCheck as CheckCircle2, ArrowRight, FileCheck, Footprints, Eye as EyeCheck, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const accessChallenges = [{
  title: "Victorian & Edwardian terraces",
  description: "Careful chimney, parapet, and roof access solutions designed to protect historic brickwork, decorative facades, and neighbouring properties.",
  icon: Building2
}, {
  title: "Converted flats & multi-occupancy",
  description: "Clear access planning that keeps communal hallways, fire exits, and residential entryways unobstructed throughout the project.",
  icon: Users
}, {
  title: "Narrow passages & lightwells",
  description: "Compact tube and fitting configurations built to navigate tight side returns, internal courtyards, and basement lightwells.",
  icon: Footprints
}, {
  title: "Busy pavements & commercial frontages",
  description: "Pedestrian protection fans, debris netting, and council pavement licence coordination for mixed residential and commercial streets.",
  icon: EyeCheck
}];
const safetyProtocols = [{
  title: "Pre-erection site assessment",
  description: "We survey ground conditions, structural tie points, overhead hazards, and pavement clearance before delivering materials."
}, {
  title: "Safe access planning",
  description: "Every scaffold is erected using TG20 compliant guidelines and compliant ladder access towers or internal staircases."
}, {
  title: "Handover inspection & Scafftag",
  description: "A thorough post-build check confirms stability before issuing formal handover certificates and dated safety tags."
}];
const accreditations = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation",
  detail: "Full adherence to industry best practice standards and audit requirements."
}, {
  name: "CHAS",
  label: "Contractors Health & Safety Assessment Scheme",
  detail: "Accredited contractor compliance across health, safety, and risk management."
}];
const nearbyAreas = [{
  name: "Wandsworth",
  to: "/scaffolding-in-wandsworth"
}, {
  name: "Richmond",
  to: "/scaffolding-in-richmond"
}, {
  name: "Kingston upon Thames",
  to: "/scaffolding-in-kingston-upon-thames"
}];
export default function AccessSafety() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="claphamaccesssafety" data-nav="light" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">
            Clapham & South London Access
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Managing Clapham access constraints and site safety
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Operating across SW4 as the principal Clapham district, from tight Victorian side returns to busy high-street pavements, we assess each site in detail before erection and provide signed handover certificates on completion.
          </p>
        </div>

        {/* Access Context Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {accessChallenges.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow]">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-md bg-accent flex items-center justify-center text-primary mb-3">
                      <IconComponent className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Safety & Accreditations Two-Column Area */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Safety Protocols */}
          <div className="lg:col-span-7 flex flex-col justify-between rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
            <div>
              <div className="flex items-center gap-2 mb-2 text-primary">
                <ShieldCheck className="size-5" />
                <span className="text-sm font-semibold uppercase tracking-wider">
                  Site Safety Process
                </span>
              </div>
              <h3 className="text-2xl font-semibold text-card-foreground font-default mb-3">
                Rigorous safety at every stage
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Our operatives follow structured inspection routines to keep workers, homeowners, and members of the public safe throughout every phase of access.
              </p>

              <div className="space-y-5">
                {safetyProtocols.map((protocol, i) => <div key={protocol.title} data-index={i} className="flex items-start gap-3.5">
                    <div className="size-6 rounded-full bg-accent text-primary flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 className="size-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-card-foreground">
                        {protocol.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                        {protocol.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-8 mt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ClipboardCheck className="size-4 text-primary" />
                <span>Statutory 7-day re-inspections available on request</span>
              </div>
              <Button asChild size="sm">
                <Link to="/HealthSafetyAccreditationsPage">
                  View safety details
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Accreditations & Compliance */}
          <div className="lg:col-span-5 flex flex-col justify-between rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
            <div>
              <div className="flex items-center gap-2 mb-2 text-primary">
                <FileCheck className="size-5" />
                <span className="text-sm font-semibold uppercase tracking-wider">
                  Accreditations
                </span>
              </div>
              <h3 className="text-2xl font-semibold text-card-foreground font-default mb-3">
                Industry-verified standards
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                We maintain active certifications with the sector's recognized bodies to assure regulatory compliance and accountability.
              </p>

              <div className="space-y-4">
                {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="rounded-lg border border-border bg-background p-4 flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-base text-foreground tracking-wide">
                        {acc.name}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        Verified
                      </Badge>
                    </div>
                    <span className="text-xs font-medium text-foreground">
                      {acc.label}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {acc.detail}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Fully insured with comprehensive public and employer liability for high-density London settings.
              </p>
            </div>
          </div>
        </div>

        {/* Nearby Areas Coverage Strip */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <MapPin className="size-4 text-primary shrink-0" />
              <span>Nearby areas covered:</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {nearbyAreas.map((area, i) => <div key={area.name} data-index={i} className="inline-flex items-center gap-3">
                  <Link to={area.to} className="text-primary hover:underline font-medium text-sm">
                    {area.name}
                  </Link>
                  {i < nearbyAreas.length - 1 && <span className="text-border" aria-hidden="true">
                      •
                    </span>}
                </div>)}
            </div>
          </div>

          <Button asChild variant="outline" size="sm">
            <Link to="#">
              View all areas
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}